import type { SearchTestbedCandidate } from "@/lib/staffingTypes";

/**
 * Raw external candidates have no id, so dedupe on the strongest signal
 * present. `socialProfiles` is checked before contact details because a social
 * job's hits are keyed by profile URL far more often than by email/phone.
 *
 * DEV-501: shared by `useSocialJobPolls` (which dedupes late hits against each
 * other) and by RawSearchPanel (which must additionally dedupe a late hit
 * against an in-request hit the hook never saw). Both MUST use the same key or
 * the panel can render the same person twice.
 */
/**
 * What to SHOW for a candidate whose name may be empty.
 *
 * DEV-653's `unknown` provider tier admits a page that clears every hard reject
 * and names nobody, on the sole condition that a contact was extracted — and it
 * emits `displayName: ""` rather than inventing a name, because naming a
 * listing after its own ad copy is the measured failure that ticket exists to
 * close. `pageEligibility.ts` states the intended consequence outright: "an
 * `unknown` row survives on its contact and is displayed by it."
 *
 * So the fallback is the CONTACT, not a placeholder word. A row reduced to
 * "Unnamed provider" is indistinguishable from every other one in a list; a row
 * showing its phone is the thing an admin would act on anyway. These are
 * admin-only surfaces, which already display contact details, so this shows
 * nothing new — R1 keeps sourced contacts away from quest CREATORS, not from
 * the staffing admin.
 *
 * Returns `null` rather than a placeholder string so the caller supplies a
 * translated one; `lib/` has no `t`. That case should be unreachable through
 * the funnel, since the tier requires a contact to exist at all.
 *
 * ONE function, used by every surface that renders a candidate's name. The
 * recurring defect in this subsystem is the same rule reimplemented in several
 * places and then drifting apart.
 */
export function candidateDisplayIdentity(c: {
  fullName?: string | null;
  phone?: string | null;
  email?: string | null;
}): string | null {
  // `.trim() ||` and not `??`: the classifier emits an EMPTY STRING, which `??`
  // passes straight through. That is precisely how two call sites that looked
  // guarded (`fullName ?? "(unknown)"`) still rendered blank.
  return (c.fullName ?? "").trim() || c.phone || c.email || null;
}

export function candidateKey(c: SearchTestbedCandidate): string {
  const socialUrl = Object.values(c.socialProfiles ?? {}).find((v) => !!v);
  return (
    c.memberId ??
    c.linkedinUrl ??
    socialUrl ??
    c.email ??
    c.phone ??
    `${(c.fullName ?? "").trim().toLowerCase()}|${c.sourcePlatform ?? c.source ?? ""}`
  );
}
